import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CONTRACT_ROLES, type ContractRole, type Language } from "@talentkontor/domain";
import { api } from "../../../../data";
import { useStaffWorkspace } from "../../../../app/session";
import { Dialog, useAction } from "../../../../components/ui";
import { currentLang, t, sprachName, istCreatorKonto } from "../../../../lib/sprache";
import { importPlatformTemplate, loadContractSettings, loadOrgDaten, type TemplateRow } from "../daten";
import { VertragPapier } from "../Papier";
import { exampleValues, renderDraft, vorlagenBeispiel } from "../pruefen";
import { ROLE_LABEL } from "../modell";
import { paketFremdesRecht, paketFremdesRechtBestaetigung } from "../muster";
import { galerieKarten, loadGalerie, loadGekauftePakete, loadPakete, passtZu, type GalerieKarte } from "./bibliothek";
import { billingChange } from "../../einstellungen/data";
import { date, money } from "../../../../lib/format";
import { MusterHinweis, musterKennung, musterLandHinweis } from "../MusterHinweis";
import "./baukasten.css";

const MERKEN = "tk-galerie-arbeitsweise";
type Arbeitsweise = "broker" | "reseller" | null;
function gemerkt(): Arbeitsweise {
  try { const v = localStorage.getItem(MERKEN); return v === "broker" || v === "reseller" ? v : null; } catch { return null; }
}
function merke(v: Arbeitsweise) {
  try { if (v) localStorage.setItem(MERKEN, v); else localStorage.removeItem(MERKEN); } catch { /* ohne Speicher gilt die Wahl nur hier */ }
}

/**
 * Galerie der CreatorOffice-Vorlagen (Schritt 4, VB-07): Frage „Wie arbeitet eure Agentur?“, Karten mit Parteien, Merkmalen und
 * Sprachen, Vorschau je Rolle, „Verwenden“ übernimmt als Entwurf und öffnet ihn im Baukasten.
 */
export function Galerie({ templates, orgId, darf }: { templates: readonly TemplateRow[]; orgId: string; darf: boolean }) {
  const creatorKonto = istCreatorKonto();
  const q = useQuery({ queryKey: ["platform_contract_templates", "galerie", creatorKonto], queryFn: () => loadGalerie(api) });
  // 0201: Paketvorlagen nur, wenn dieses Konto das Paket gekauft hat (Plattform-Admins lesen die Texte auch ohne Kauf)
  const gekauft = useQuery({ queryKey: ["org_template_packages", orgId], queryFn: () => loadGekauftePakete(api, orgId) });
  const [arbeitsweise, setArbeitsweise] = useState<Arbeitsweise>(gemerkt);
  const [vorschau, setVorschau] = useState<GalerieKarte | null>(null);
  const [weitere, setWeitere] = useState(false);
  const [ersetzen, setErsetzen] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  const navigate = useNavigate();
  const ws = useStaffWorkspace();
  if (!q.data?.length) return null;
  const eigene = new Set((gekauft.data ?? []).map((g) => g.package));
  const zeilen = q.data.filter((z) => !z.package || eigene.has(z.package));
  const karten = galerieKarten(zeilen, currentLang() === "en" ? "en" : "de");
  const passend = karten.filter((k) => passtZu(k, arbeitsweise));
  const uebrig = karten.filter((k) => !passtZu(k, arbeitsweise));
  const waehle = (v: Arbeitsweise) => { setArbeitsweise(v); merke(v); };
  // „Kooperationsvertrag DE · Version 1 · Stand Oktober 2026“ (0181)
  const kennungOf = (k: GalerieKarte) => {
    const z = k.zeilen[currentLang() === "en" ? "en" : "de"] ?? Object.values(k.zeilen)[0];
    return z ? musterKennung({ kind_name_de: z.kind_name_de, kind_name_en: z.kind_name_en, version: z.version, jurisdiction: z.jurisdiction ?? null, legal_as_of: z.legal_as_of ?? null }) : null;
  };
  const rechtsordnungen = [...new Set(zeilen.map((z) => z.jurisdiction ?? "DE"))];
  const landHinweis = rechtsordnungen.map((j) => musterLandHinweis(j, ws.org.country)).find(Boolean) ?? null;

  const stand = (k: GalerieKarte, l: Language) => {
    const fam = templates.filter((x) => x.key === k.key && x.language === l);
    const zeile = k.zeilen[l];
    if (!fam.length) return { art: "neu" as const, entwurf: undefined };
    const have = Math.max(0, ...fam.map((x) => x.platform_version ?? 0));
    return { art: zeile && have < zeile.version ? ("neuer" as const) : ("uebernommen" as const), entwurf: fam.find((x) => x.status === "draft") };
  };

  const karte = (k: GalerieKarte) => {
    const st = k.sprachen.map((l) => ({ l, ...stand(k, l) }));
    const uebernommen = st.some((s) => s.art !== "neu");
    return (
      <article key={k.key} className="panel bk-karte" aria-label={k.name}>
        <div className="row between">
          <span className="bk-kuerzel" aria-hidden="true">{k.kuerzel}</span>
          {uebernommen && <span className="pill success">{t("übernommen", "taken over")}</span>}
          {st.some((s) => s.art === "neuer") && <span className="pill warning">{t("neuere Fassung", "newer version")}</span>}
        </div>
        <h4>{k.name}</h4>
        {kennungOf(k) && <p className="bk-klein"><strong>{kennungOf(k)}</strong></p>}
        <p className="bk-klein">{k.beschreibung}</p>
        <p className="bk-pills">{k.parteien.map((r) => <span key={r} className="pill neutral">{ROLE_LABEL[r]}</span>)}</p>
        {(k.merkmale.length > 0 || k.modell !== "both") && (
          <p className="bk-pills">
            {k.modell === "reseller" && <span className="pill info">{t("passt zu: selbst verkaufen", "fits: selling yourselves")}</span>}
            {k.modell === "broker" && <span className="pill info">{t("passt zu: vermitteln", "fits: brokering")}</span>}
            {k.merkmale.map((m) => <span key={m} className="pill neutral">{m}</span>)}
          </p>
        )}
        <div className="row between bk-karte-fuss">
          <span className="bk-pills">{k.sprachen.map((l) => <span key={l} className="pill neutral" title={sprachName(l)}>{l.toUpperCase()}</span>)}</span>
          <span className="row">
            <button type="button" onClick={() => setVorschau(k)}>{t("Vorschau", "Preview")}</button>
            {darf && st.map((s) => s.entwurf
              ? <Fragment key={s.l}>
                <Link className="btn" to={`/agentur/vertraege/vorlagen/${s.entwurf.id}/baukasten`}>{t(`Entwurf ${s.l.toUpperCase()}`, `Draft ${s.l.toUpperCase()}`)}</Link>
                {/* K-401: alter Entwurf, neuere Fassung des Musters: auf Nachfrage ersetzen */}
                {s.art === "neuer" && <button type="button" className="primary" disabled={!!busy} onClick={() => setErsetzen(`${k.key}/${s.l}`)}>{t(`Neue Fassung ${s.l.toUpperCase()}`, `New version ${s.l.toUpperCase()}`)}</button>}
              </Fragment>
              : s.art === "uebernommen" ? (() => {
                const aktiv = templates.filter((x) => x.key === k.key && x.language === s.l && x.status === "active")[0];
                return aktiv ? <Link key={s.l} className="btn" to={`/agentur/vertraege/vorlagen/${aktiv.id}/baukasten`}>{t(`Öffnen ${s.l.toUpperCase()}`, `Open ${s.l.toUpperCase()}`)}</Link> : null;
              })()
              : (
                <button key={s.l} type="button" className="primary" disabled={!!busy} onClick={async () => {
                  const id = await run(`galerie-${k.key}-${s.l}`, () => importPlatformTemplate(api, orgId, k.key, s.l), t("Als Entwurf übernommen.", "Taken over as a draft."));
                  if (id) navigate(`/agentur/vertraege/vorlagen/${id}/baukasten`);
                }}>{k.sprachen.length > 1 ? t(`Verwenden ${s.l.toUpperCase()}`, `Use ${s.l.toUpperCase()}`) : t("Verwenden", "Use")}</button>
              ))}
          </span>
        </div>
        {darf && st.filter((s) => s.entwurf && s.art === "neuer" && ersetzen === `${k.key}/${s.l}`).map((s) => (
          <div key={s.l} className="notice warning stack" role="alert">
            <p>{t("Der offene Entwurf wird durch die neue Fassung des Musters ersetzt. Eigene Änderungen im Entwurf gehen dabei verloren; aktive Vorlagen und bestehende Verträge bleiben unverändert.",
              "The open draft is replaced by the new version of the template. Your own changes in the draft are lost; active templates and existing contracts stay unchanged.")}</p>
            <div className="row">
              <button type="button" className="primary" disabled={!!busy} onClick={async () => {
                const id = await run(`galerie-replace-${k.key}-${s.l}`, () => importPlatformTemplate(api, orgId, k.key, s.l, true), t("Neue Fassung als Entwurf übernommen.", "New version taken over as a draft."));
                setErsetzen(null);
                if (id) navigate(`/agentur/vertraege/vorlagen/${id}/baukasten`);
              }}>{t("Ja, Entwurf ersetzen", "Yes, replace draft")}</button>
              <button type="button" disabled={!!busy} onClick={() => setErsetzen(null)}>{t("Abbrechen", "Cancel")}</button>
            </div>
          </div>
        ))}
      </article>
    );
  };

  return (
    <section className="stack" aria-label={t("CreatorOffice-Vorlagen", "CreatorOffice templates")}>
      {/* Creator-Konto: alle Muster passen (der Creator ist selbst Partei), die Frage nach der Arbeitsweise der Agentur entfällt */}
      {!creatorKonto && <div className="panel bk-arbeitsweise">
        <div>
          <h3>{t("Wie arbeitet eure Agentur?", "How does your agency work?")}</h3>
          <p className="bk-klein">{t("Danach richten sich die Vorschläge.", "The suggestions follow from this.")}</p>
        </div>
        <div role="group" aria-label={t("Arbeitsweise", "Way of working")} className="bk-wahlkarten">
          <button type="button" aria-pressed={arbeitsweise === "broker"} onClick={() => waehle(arbeitsweise === "broker" ? null : "broker")}>
            <strong>{t("Wir vermitteln", "We broker")}</strong><span>{t("Der Vertrag läuft zwischen Marke und Creator, wir betreuen und rechnen ab.", "The contract is between brand and creator; we manage and invoice.")}</span>
          </button>
          <button type="button" aria-pressed={arbeitsweise === "reseller"} onClick={() => waehle(arbeitsweise === "reseller" ? null : "reseller")}>
            <strong>{t("Wir verkaufen selbst", "We sell ourselves")}</strong><span>{t("Die Marke beauftragt uns, wir beauftragen den Creator.", "The brand commissions us; we commission the creator.")}</span>
          </button>
        </div>
      </div>}
      <div className="row between">
        <h3>{t("CreatorOffice-Vorlagen", "CreatorOffice templates")}</h3>
        <small>{t("Übernommen wird immer als Entwurf.", "Always taken over as a draft.")}</small>
      </div>
      {/* 0181 (Anwalt Punkt 13): unverbindliche Muster mit Rechtsordnung; Konten in anderen Ländern bekommen den Hinweis */}
      <MusterHinweis immer />
      {landHinweis && <p className="notice warning" role="note">{landHinweis}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <PaketRueckkehr orgId={orgId} />
      <div className="bk-galerie">{passend.map(karte)}</div>
      <Vorlagenpakete orgId={orgId} />
      {uebrig.length > 0 && (
        <>
          <button type="button" aria-expanded={weitere} onClick={() => setWeitere((v) => !v)}>{weitere ? t("Weitere Vorlagen ausblenden", "Hide more templates") : t(`Weitere Vorlagen zeigen (${uebrig.length})`, `Show more templates (${uebrig.length})`)}</button>
          {weitere && <div className="bk-galerie">{uebrig.map(karte)}</div>}
        </>
      )}
      <Vorschau karte={vorschau} onClose={() => setVorschau(null)} orgId={orgId} />
    </section>
  );
}

/**
 * Vorlagenpakete (0201): gesperrte Karte mit Inhalt, Preis und Kauf über Stripe (einmalige Zahlung, nur Admins). Gekauft: kurzer
 * Hinweis; die Vorlagen des Pakets erscheinen dann als normale Karten in der Galerie (markiert mit „Vorlagenpaket“).
 */
function Vorlagenpakete({ orgId }: { orgId: string }) {
  const ws = useStaffWorkspace();
  const pakete = useQuery({ queryKey: ["template_packages", istCreatorKonto()], queryFn: () => loadPakete(api) });
  const gekauft = useQuery({ queryKey: ["org_template_packages", orgId], queryFn: () => loadGekauftePakete(api, orgId) });
  const { busy, error, run } = useAction();
  // K-247: Paket mit deutschem Recht; außerhalb Deutschlands Hinweis und Bestätigung vor dem Kauf
  const fremd = paketFremdesRecht("pro_paket", ws.org.country ?? null);
  const [ok, setOk] = useState(false);
  if (!pakete.data?.length || !gekauft.data) return null;
  const en = currentLang() === "en";
  return (
    <>
      {pakete.data.map((p) => {
        const name = en ? p.name_en : p.name_de;
        const kauf = gekauft.data.find((g) => g.package === p.key);
        if (kauf) {
          return <p key={p.key} className="notice success" role="note">{t(`„${name}“ ist freigeschaltet (gekauft am ${date(kauf.purchased_at)}). Die Vorlagen stehen oben in der Galerie, markiert mit „Vorlagenpaket“.`,
            `“${name}” is unlocked (bought on ${date(kauf.purchased_at)}). The templates are in the gallery above, marked “template pack”.`)}</p>;
        }
        const preis = money(p.price_cents, p.currency);
        return (
          <article key={p.key} className="panel bk-paket" aria-label={name}>
            <div className="row between">
              <span className="pill info">{t("Vorlagenpaket", "Template pack")}</span>
              <span className="bk-paket-preis"><strong>{preis}</strong> <small>{t("netto, einmalig", "net, one-off")}</small></span>
            </div>
            <h4>{name}</h4>
            <p className="bk-klein">{en ? p.description_en : p.description_de}</p>
            <ul className="bk-paket-liste">{p.contents.map((c, i) => <li key={i}>{en ? c.en : c.de}</li>)}</ul>
            {/* K-403b: das Paket ist seit 0276 länderneutral (DACH), Recht und Steuerbegriffe kommen aus den Firmenangaben */}
            <p className="bk-klein"><strong>{t("Rechtsordnung: Deutschland, Österreich oder Schweiz", "Jurisdiction: Germany, Austria or Switzerland")}</strong> {t("(nach dem anwendbaren Recht in euren Firmenangaben)", "(according to the governing law in your company details)")}</p>
            {fremd && (
              <div className="notice warning stack" role="alert">
                <p>{fremd}</p>
                <label className="row tk-check"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} /><span>{paketFremdesRechtBestaetigung()}</span></label>
              </div>
            )}
            <p className="bk-klein">{t("Auch gekaufte Vorlagen sind unverbindliche Muster: Ihr passt sie an euren Fall an und verantwortet ihren Einsatz selbst (AGB, Abschnitt Leistungen).",
              "Purchased templates are non-binding samples too: you adapt them to your case and are responsible for their use (terms, section Services).")}</p>
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="row">
              {ws.isChef ? (
                <button type="button" className="primary" disabled={!!busy || (!!fremd && !ok)} onClick={async () => {
                  await run(`paket-${p.key}`, async () => {
                    const antwort = await billingChange(api, orgId, { action: "package_checkout", package: p.key });
                    // Ohne Adresse sonst stiller Fehlschlag ohne Meldung (Prüfung 24.10.2026)
                    if (!antwort.url || !/^https:\/\//.test(antwort.url)) throw new Error(t("Die Zahlungsseite ließ sich nicht öffnen. Bitte gleich noch einmal versuchen.", "The payment page could not be opened. Please try again shortly."));
                    window.location.assign(antwort.url);
                    // Sperre bis zum Wechsel auf die Stripe-Seite halten; zurück aus dem bfcache gibt useAction sie wieder frei
                    await new Promise<never>(() => undefined);
                  });
                }}>{busy ? t("Zahlung wird geöffnet …", "Opening payment …") : t(`Für ${preis} kaufen`, `Buy for ${preis}`)}</button>
              ) : <small>{t("Kaufen kann der Admin eures Kontos.", "Your account admin can buy it.")}</small>}
            </div>
          </article>
        );
      })}
    </>
  );
}

/** Rückkehr aus dem Paketkauf (?paket=erfolg&session_id=… bzw. abgebrochen): Kauf übernehmen, falls der Webhook ausbleibt, und neu laden. */
function PaketRueckkehr({ orgId }: { orgId: string }) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const status = params.get("paket");
  const session = params.get("session_id");
  const [stand, setStand] = useState<"busy" | "done" | "failed" | null>(null);
  const einmal = useRef(false);
  useEffect(() => {
    if (status !== "erfolg" || einmal.current) return;
    einmal.current = true;
    const reload = () => {
      void qc.invalidateQueries({ queryKey: ["platform_contract_templates"] });
      void qc.invalidateQueries({ queryKey: ["org_template_packages", orgId] });
    };
    setStand("busy");
    const ok = session && /^cs_(test|live)_[A-Za-z0-9]{10,200}$/.test(session);
    (ok ? billingChange(api, orgId, { action: "checkout_confirm", session_id: session }) : Promise.resolve(null))
      .then(() => { setStand("done"); reload(); })
      .catch(() => { setStand("failed"); });
    const timers = [4000, 15000].map((ms) => setTimeout(reload, ms));
    return () => timers.forEach(clearTimeout);
  }, [status, session, orgId, qc]);
  if (!status) return null;
  const weg = () => { const n = new URLSearchParams(params); n.delete("paket"); n.delete("session_id"); setParams(n, { replace: true }); };
  if (status === "abgebrochen") {
    return <p className="notice" role="status">{t("Der Kauf wurde abgebrochen. Es ist nichts berechnet.", "The purchase was cancelled. Nothing has been charged.")} <button type="button" className="tk-linkbtn" onClick={weg}>{t("Ausblenden", "Dismiss")}</button></p>;
  }
  if (status !== "erfolg") return null;
  return (
    <p className={stand === "failed" ? "notice warning" : "notice success"} role="status">
      {stand === "failed"
        ? t("Danke! Die Zahlung ist eingegangen, die Freischaltung dauert noch einen Moment. Lade die Seite gleich neu.", "Thank you! The payment has been received; unlocking takes a moment. Reload the page shortly.")
        : stand === "done"
          ? t("Danke! Das Vorlagenpaket ist freigeschaltet. Die Rechnung kommt per Mail von Stripe.", "Thank you! The template pack is unlocked. The invoice comes by email from Stripe.")
          : t("Danke! Die Zahlung wird bestätigt …", "Thank you! Confirming the payment …")}
      {" "}<button type="button" className="tk-linkbtn" onClick={weg}>{t("Ausblenden", "Dismiss")}</button>
    </p>
  );
}

function Vorschau({ karte, onClose, orgId }: { karte: GalerieKarte | null; onClose: () => void; orgId: string }) {
  const [lang, setLang] = useState<Language>(currentLang() === "en" ? "en" : "de");
  const [rolle, setRolle] = useState<ContractRole>("agency");
  const ws = useStaffWorkspace();
  const settings = useQuery({ queryKey: ["contract_settings", orgId], queryFn: () => loadContractSettings(api, orgId) });
  const org = useQuery({ queryKey: ["org_daten", orgId], queryFn: () => loadOrgDaten(api, orgId) });
  const zeile = karte ? karte.zeilen[lang] ?? Object.values(karte.zeilen)[0] : undefined;
  const beispiel = zeile ? vorlagenBeispiel(org.data, zeile.language, settings.data?.number_format ?? "V-{NNNN}", new Date().getFullYear()) : null;
  const r = zeile && beispiel ? renderDraft({ body: zeile.body_html, annex: zeile.annex_html, defs: zeile.variables, values: exampleValues(zeile.variables), context: beispiel.context, locale: beispiel.locale, currency: beispiel.currency }, rolle, zeile.annex_hidden_from, zeile.required_signers) : null;
  return (
    <Dialog open={!!karte} onClose={onClose} title={karte ? t(`Vorschau: ${karte.name}`, `Preview: ${karte.name}`) : ""} wide>
      {karte && zeile && r && (
        <div className="stack">
          <div className="row">
            <span role="group" aria-label={t("Sprache", "Language")}>{karte.sprachen.map((l) => <button key={l} type="button" aria-pressed={zeile.language === l} onClick={() => setLang(l)}>{sprachName(l)}</button>)}</span>
            <span role="group" aria-label={t("Vorschau als", "Preview as")}>{CONTRACT_ROLES.map((x) => <button key={x} type="button" aria-pressed={rolle === x} onClick={() => setRolle(x)}>{ROLE_LABEL[x]}</button>)}</span>
          </div>
          <VertragPapier logoPath={ws.org.logo_path} accent={ws.org.brand_color} html={r.html} annexHtml={r.annexHtml} defs={zeile.variables} lang={zeile.language} eyebrow={t(`Vorschau als ${ROLE_LABEL[rolle]} · Beispielwerte`, `Preview as ${ROLE_LABEL[rolle]} · sample values`)}
            marks={CONTRACT_ROLES.map((x) => ({ role: x, signed: false }))} />
        </div>
      )}
    </Dialog>
  );
}
